import PropTypes from "prop-types";
import { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";

// Styles
import "./ImgSelectDialog.css";

// Store
import useBrainStore from "@/store/_brainEmStore";
import useLinkStore from "@/store/_linkStore";
import { organismImgData } from "@/data/OrganismImgData";

// Components
import ImgBtn from "@/components/ImgBtn/ImgBtn";

// -----------------------------------------------------------------------------

export default function ImageSelectDialog({ isLinkMode, onCancel, onConfirm }) {
	// Determine which store hook to use
	const useStore = isLinkMode ? useLinkStore : useBrainStore;
	const selectedOrganism = useStore((state) => state.organism);

	const [selectedStyle, setSelectedStyle] = useState("sciIllustration"); // onConfirm will send style to parent component

	// Get img data based on selected organism to populate radio buttons
	const {
		imageSci,
		classNameSci,
		altSci,
		citationSci,
		imagePhoto,
		classNamePhoto,
		altPhoto,
		citationPhoto,
	} = organismImgData[selectedOrganism];

	const popoverRef = useRef(null);
	const confirmedAction = useRef(false); // Ref to track if closure is due to confirm

	// -----------------------------------------------------------------------------

	useEffect(() => {
		const popoverEl = popoverRef.current;
		if (!popoverEl) return;

		// Handler for the toggle event
		function handleToggle(event) {
			if (event.newState === "closed") {
				// If closed and not by a confirm action, call onCancel
				if (!confirmedAction.current) {
					onCancel();
				}
				// Reset the flag for next time
				confirmedAction.current = false;
			}
		}

		popoverEl.addEventListener("toggle", handleToggle);

		// Show popover when it first mounts
		// Use try-catch for showPopover as it can throw if already shown, though unlikely here.
		try {
			popoverEl.showPopover();
		} catch (error) {
			console.error("Error showing popover:", error);
			// Fallback or error handling if needed, though on initial mount this should be fine.
		}

		return () => {
			popoverEl.removeEventListener("toggle", handleToggle);
			// Ensure popover is hidden on unmount if still open
			// Check if it's still in the DOM and open before trying to hide
			if (
				document.body.contains(popoverEl) &&
				popoverEl.matches(":popover-open")
			) {
				try {
					popoverEl.hidePopover();
				} catch (error) {
					console.error("Error hiding popover on unmount:", error);
				}
			}
		};
	}, [onCancel]); // Dependency: onCancel in case it changes, though typically stable

	// -----------------------------------------------------------------------------

	// Handler for the confirm/export button
	function handleConfirm() {
		confirmedAction.current = true; // Mark that closure is due to confirm
		if (popoverRef.current) {
			try {
				popoverRef.current.hidePopover();
			} catch (error) {
				console.error("Error hiding popover on confirm:", error);
			}
		}
		onConfirm(selectedStyle); // Pass the selected style value back
	}

	// Handler for the cancel button
	function handleCancel() {
		// confirmedAction remains false (or is reset by toggle if already closed)
		if (popoverRef.current) {
			try {
				popoverRef.current.hidePopover();
			} catch (error) {
				console.error("Error hiding popover on cancel:", error);
			}
		}
		// onCancel will be called by the 'toggle' event listener when the popover closes
		// or directly if an immediate action is required before the toggle event.
		// For consistency, let the toggle event handle calling onCancel.
		// If onCancel needs to be called *before* the popover fully hides, call it here too.
		// However, current logic calls onCancel in SummaryBtnGroup to unmount this component.
		// So the toggle event listener calling onCancel is the primary path.
	}

	// -----------------------------------------------------------------------------

	return (
		// The main element is now a div with the popover attribute
		<motion.div
			ref={popoverRef}
			popover="auto"
			id="imageSelectPopover"
			className="image-select-popover"
			initial={{ x: "-100%" }}
			animate={{ x: "0%" }}
			exit={{ x: "-100%" }}
			transition={{
				ease: "easeOut",
				duration: 0.2,
			}}
		>
			{/* Modal content ------------------------------------------------------- */}
			<div className="header-and-content">
				{/* Header */}
				<header>
					<h5>Select style</h5>
				</header>

				<div className="dialog-content">
					{/* Img btns  */}
					<fieldset
						className="img-btn-group dialog-radio-btn-group"
						aria-label="Select image style"
					>
						{/* Scientific illustration -------------------------------------- */}
						<ImgBtn
							className={classNameSci}
							image={imageSci}
							alt={altSci}
							citation={citationSci}
							label="Scientific"
							value="sciIllustration"
							name="imgStyle"
							checked={selectedStyle === "sciIllustration"}
							onChange={(e) => setSelectedStyle(e.target.value)} // set state to 'sciIllustration'
						/>

						{/* Photo -------------------------------------------------------- */}
						<ImgBtn
							className={classNamePhoto}
							image={imagePhoto}
							alt={altPhoto}
							citation={citationPhoto}
							label="Photo"
							value="photo"
							name="imgStyle"
							checked={selectedStyle === "photo"}
							onChange={(e) => setSelectedStyle(e.target.value)} // set state to 'photo'
						/>
					</fieldset>

					{/* Image credits --------------------------------------------------- */}
					<p>
						Kudos to BioRender for providing the scientific illustration, and to{" "}
						{citationPhoto} for the photo.
					</p>
				</div>
			</div>

			{/* Bottom btn group ---------------------------------------------------- */}
			<div className="dialog-btn-group">
				{/* Cancel btn */}
				<button type="button" onClick={handleCancel}>
					Cancel
				</button>

				{/* Export btn */}
				<button className="export-btn" type="button" onClick={handleConfirm}>
					Export
				</button>
			</div>
		</motion.div>
	);
}

// -----------------------------------------------------------------------------

ImageSelectDialog.propTypes = {
	isLinkMode: PropTypes.bool.isRequired,
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
};
